import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Flex, IconButton, Tooltip, useToast } from '@bonusly/design-system';

import type { BizyUiBlockAction } from '../../../../lib/bizy/types/ui-blocks';
import { useBizyUiBlockActionDispatcher } from '../context/actions-context';
import { CheckOutline, DuplicateOutline } from './icons';

type ActionVariant = { variant: 'solid' | 'ghost'; colorScheme: 'primary' | 'secondary' };

/**
 * Per-action visual treatment. The committing action (Give now / Send now,
 * confirm, request draft) is a solid PRIMARY button; the give-box escape hatch
 * is a SECONDARY button; navigation / dismiss actions are ghost secondary.
 */
export function variantFor(action: BizyUiBlockAction): ActionVariant {
	switch (action.type) {
		case 'send_now':
		case 'commit_user_action':
		case 'request_draft':
			return { variant: 'solid', colorScheme: 'primary' };
		case 'insert_into_draft':
			return { variant: 'ghost', colorScheme: 'primary' };
		case 'open_in_givebox':
			return { variant: 'solid', colorScheme: 'secondary' };
		case 'view_post':
		case 'dismiss':
		case 'cancel_user_action':
		case 'attach_gif':
		case 'draft_doodle':
		case 'attach_doodle':
		case 'show_gif_picker':
		case 'wrap_post':
			return { variant: 'ghost', colorScheme: 'secondary' };
	}
}

// Ghost actions sort first (left); the solid commit button lands on the right.
function actionWeight(action: BizyUiBlockAction): number {
	return variantFor(action).variant === 'ghost' ? 0 : 1;
}

/** Prefer `action.label`; otherwise fall back to a translated default per action type. */
function useActionLabel(action: BizyUiBlockAction): string {
	const { t } = useTranslation('admin_bizy');
	if (action.label) return action.label;
	switch (action.type) {
		case 'insert_into_draft':
			return t('uiBlocks.actions.insertIntoDraft');
		case 'send_now':
			return t('uiBlocks.actions.sendNow');
		case 'open_in_givebox':
			return t('uiBlocks.actions.openInGivebox');
		case 'view_post':
			return t('uiBlocks.actions.viewPost');
		case 'dismiss':
			return t('uiBlocks.actions.dismiss');
		case 'commit_user_action':
			return t('uiBlocks.actions.confirm');
		case 'cancel_user_action':
			return t('uiBlocks.actions.cancel');
		case 'request_draft':
			return t('uiBlocks.actions.requestDraft');
		case 'attach_gif':
		case 'draft_doodle':
		case 'attach_doodle':
		case 'show_gif_picker':
		case 'wrap_post':
			return '';
	}
}

/**
 * Action bar rendered at the bottom of a UI block card. Renders a left-side
 * copy-to-clipboard icon (when `copyText` is provided) and a right-aligned
 * row of action buttons, each dispatched through the host's action handler.
 */
export function UiBlockActionBar({
	actions,
	copyText,
	leftAccessory,
	areActionsDisabled = false,
	oneShot = false,
}: {
	actions: readonly BizyUiBlockAction[];
	copyText?: string;
	/** Rendered on the left where the copy button sits (e.g. the recognition draft card's wand menu). Replaces the copy button when present. */
	leftAccessory?: ReactNode;
	areActionsDisabled?: boolean;
	oneShot?: boolean;
}) {
	const dispatch = useBizyUiBlockActionDispatcher();
	const [hasActed, setHasActed] = useState(false);
	const actedRef = useRef(false);

	if (actions.length === 0 && !copyText && !leftAccessory) return null;

	const ordered = [...actions].sort((a, b) => actionWeight(a) - actionWeight(b));
	const buttonsDisabled = areActionsDisabled || (oneShot && hasActed);

	const handleAction = (action: BizyUiBlockAction) => {
		if (oneShot) {
			if (actedRef.current) return;
			actedRef.current = true;
			setHasActed(true);
		}
		dispatch(action);
	};

	return (
		<Flex justify="space-between" align="center" gap={2} rowGap={2} mt={3} flexWrap="wrap">
			{(leftAccessory || copyText) && (
				<Flex align="center" flexShrink={0}>
					{leftAccessory ?? (copyText ? <CopyButton text={copyText} /> : null)}
				</Flex>
			)}
			<Flex align="center" gap={2} rowGap={2} flexWrap="wrap" justify="flex-end" flex="1 1 auto" minW={0}>
				{ordered.map((action, index) => (
					<ActionButton key={`${action.type}-${index}`} action={action} isDisabled={buttonsDisabled} onAction={handleAction} />
				))}
			</Flex>
		</Flex>
	);
}

function CopyButton({ text }: { text: string }) {
	const { t } = useTranslation('admin_bizy');
	const toast = useToast();
	const [copied, setCopied] = useState(false);
	const handleCopy = () => {
		navigator.clipboard
			.writeText(text)
			.then(() => {
				setCopied(true);
				setTimeout(() => setCopied(false), 2000);
			})
			.catch(() => {
				toast({ title: t('feedback.copyFailed'), status: 'error', duration: 2000 });
			});
	};
	return (
		<Tooltip label={copied ? t('feedback.copied') : t('feedback.copy')} placement="top">
			<IconButton
				aria-label={t('feedback.copy')}
				icon={copied ? <CheckOutline boxSize={4} /> : <DuplicateOutline boxSize={4} />}
				size="sm"
				variant="ghost"
				colorScheme="secondary"
				color="text.softer"
				onClick={handleCopy}
			/>
		</Tooltip>
	);
}

function ActionButton({ action, isDisabled, onAction }: { action: BizyUiBlockAction; isDisabled: boolean; onAction: (action: BizyUiBlockAction) => void }) {
	const label = useActionLabel(action);
	const { variant, colorScheme } = variantFor(action);
	return (
		<Button variant={variant} colorScheme={colorScheme} size="sm" flexShrink={0} onClick={() => onAction(action)} isDisabled={isDisabled}>
			{label}
		</Button>
	);
}
